import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { projects } from "@/lib/portfolio";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ResponsiveImg } from "@/components/ResponsiveImg";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";
import {
  projectImages,
  projectLogos,
  projectExtraLogos,
  internalToPublicSlug,
  caseOgImages,
  cudoNvidiaFeature,
} from "@/lib/work-data";

export const Route = createFileRoute("/work/")({
  head: () => ({
    meta: [
      { title: "Case Studies | Scott Cunningham" },
      {
        name: "description",
        content:
          "Case studies by Scott Cunningham (@scottcbusiness) in content strategy, copywriting, social media, AI workflows, Web3, cloud, and growth.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Case Studies | Scott Cunningham" },
      {
        property: "og:description",
        content:
          "Case studies by Scott Cunningham (@scottcbusiness) in content strategy, copywriting, social media, AI workflows, Web3, cloud, and growth.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/work" },
      { property: "og:image", content: "https://scottcbusiness.com/og-default.jpg" },
      { property: "og:image:alt", content: "Scott Cunningham, Content and Social Media Strategy" },
        { name: "twitter:image:alt", content: "Scott Cunningham, Content and Social Media Strategy" },
      { property: "og:site_name", content: "Scott Cunningham" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Case Studies | Scott Cunningham" },
      {
        name: "twitter:description",
        content:
          "Case studies by Scott Cunningham (@scottcbusiness) in content strategy, copywriting, social media, AI workflows, Web3, cloud, and growth.",
      },
      { name: "twitter:image", content: "https://scottcbusiness.com/og-default.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://scottcbusiness.com/work" },
      {
        rel: "preload",
        as: "image",
        href: cudoNvidiaFeature.img.src,
        imagesrcset: cudoNvidiaFeature.sources.webp,
        imagesizes: "(min-width: 1024px) 560px, 100vw",
        fetchPriority: "high" as const,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "Case Studies | Scott Cunningham",
              url: "https://scottcbusiness.com/work",
              description:
                "Case studies by Scott Cunningham (@scottcbusiness) in content strategy, copywriting, social media, AI workflows, Web3, cloud, and growth.",
              isPartOf: { "@id": "https://scottcbusiness.com/#website" },
              about: { "@id": "https://scottcbusiness.com/#person" },
              mainEntity: {
                "@type": "ItemList",
                itemListElement: projects.map((p, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `https://scottcbusiness.com/work/${internalToPublicSlug[p.slug]}`,
                  item: {
                    "@type": "CreativeWork",
                    "@id": `https://scottcbusiness.com/work/${internalToPublicSlug[p.slug]}`,
                    name: p.name,
                    headline: p.name,
                    description: p.summary,
                    about: p.sector,
                    url: p.url,
                    image: caseOgImages[p.slug],
                    creator: { "@id": "https://scottcbusiness.com/#person" },
                    author: { "@id": "https://scottcbusiness.com/#person" },
                  },
                })),
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://scottcbusiness.com/" },
                { "@type": "ListItem", position: 2, name: "Work", item: "https://scottcbusiness.com/work" },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: WorkIndex,
});

const topMetricCount = 4;

function CaseStudyNav() {
  return (
    <nav className="mt-10" aria-label="Case studies">
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        Jump to case study
      </p>
      <ul className="flex flex-wrap gap-3">
        {projects.map((p) => {
          const slug = internalToPublicSlug[p.slug];
          const isThunderbolt = p.slug === "thunderbolt";
          return (
            <li key={p.slug}>
              <a
                href={`#case-study-${slug}`}
                className="relative inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground backdrop-blur-sm transition-all duration-300 ease-out hover:scale-105 hover:text-foreground hover:shadow-pop-sm gradient-border-2-hover"
              >
                {isThunderbolt ? (
                  <>
                    <span className="inline-flex items-center gap-1.5">
                      <img
                        src={projectExtraLogos.thunderbolt?.[0]}
                        alt=""
                        className="h-4 w-4 object-contain"
                        loading="lazy"
                      />
                      <span>Thunderbolt</span>
                    </span>
                    <span className="text-muted-foreground/60">+</span>
                    <span className="inline-flex items-center gap-1.5">
                      <img
                        src={projectLogos.thunderbolt}
                        alt=""
                        className="h-4 w-4 object-contain"
                        loading="lazy"
                      />
                      <span>Intel</span>
                    </span>
                  </>
                ) : (
                  <>
                    <img
                      src={projectLogos[p.slug]}
                      alt=""
                      className="h-4 w-4 object-contain"
                      loading="lazy"
                    />
                    {p.name}
                  </>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}


function WorkIndex() {
  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-border grain">
        <LightSectionBackdrop variant="analytics" arcs={false} />
        <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
        <div
          className="blob float-slow right-[-120px] top-20 h-[360px] w-[360px] bg-[var(--color-violet)]"
          style={{ animationDelay: "-3s" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="sr-only">
            <Breadcrumbs crumbs={[{ label: "Home", to: "/" }, { label: "Work" }]} />
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Selected work
          </p>
          <h1 className="mt-6 font-display text-[clamp(2rem,4.6vw,4.5rem)] leading-[1.05] pb-2">
            Five case studies.
            <br />
            <span className="inline-block md:whitespace-nowrap">
              <span className="inline-block whitespace-nowrap">
                <em className="inline-block italic text-gradient italic-bleed">One throughline</em>
                <span>:</span>
              </span>{" "}
              <span className="inline-block whitespace-nowrap">measurable growth.</span>
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            A scannable index. Click any card for the full case study: metrics, visuals, and the story behind the work.
          </p>

          <CaseStudyNav />
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-border">
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        {/* Floating mid-section background accents behind the case studies */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[-10%] top-[28%] -z-10 h-[520px] w-[520px] rounded-full opacity-20"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, color-mix(in oklab, #f97316 40%, transparent), transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-[-12%] top-[52%] -z-10 h-[480px] w-[480px] rounded-full opacity-[0.18]"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, color-mix(in oklab, #a855f7 40%, transparent), transparent 70%)",
            filter: "blur(55px)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[15%] bottom-[10%] -z-10 h-[420px] w-[420px] rounded-full opacity-[0.16]"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, color-mix(in oklab, #6366f1 40%, transparent), transparent 70%)",
            filter: "blur(50px)",
          }}
        />
        <svg
          className="pointer-events-none absolute left-[6%] top-[38%] -z-10 h-40 w-40 opacity-15"
          viewBox="0 0 160 160"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="80" cy="80" r="75" stroke="url(#work-mid-arc1)" strokeWidth="1" />
          <circle cx="80" cy="80" r="55" stroke="url(#work-mid-arc1)" strokeWidth="0.75" opacity="0.6" />
          <defs>
            <linearGradient id="work-mid-arc1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </svg>
        <svg
          className="pointer-events-none absolute right-[8%] top-[22%] -z-10 h-32 w-32 opacity-15"
          viewBox="0 0 128 128"
          fill="none"
          aria-hidden="true"
        >
          <polygon
            points="64,8 120,40 120,88 64,120 8,88 8,40"
            stroke="url(#work-mid-hex)"
            strokeWidth="1.5"
          />
          <defs>
            <linearGradient id="work-mid-hex" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
          </defs>
        </svg>
        <svg
          className="pointer-events-none absolute right-[10%] bottom-[34%] -z-10 h-36 w-36 opacity-15"
          viewBox="0 0 144 144"
          fill="none"
          aria-hidden="true"
        >
          <g stroke="url(#work-mid-lines)" strokeWidth="2" strokeLinecap="round">
            <path d="M 20 124 L 124 20" />
            <path d="M 40 124 L 124 40" strokeWidth="1.5" opacity="0.7" />
            <path d="M 60 124 L 124 60" opacity="0.5" />
          </g>
          <defs>
            <linearGradient id="work-mid-lines" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0%" stopColor="#ec4899" />
              <stop offset="100%" stopColor="#f97316" />
            </linearGradient>
          </defs>
        </svg>
        <span
          className="pointer-events-none absolute left-[22%] top-[18%] -z-10 text-xl text-orange-500/40 animate-pulse"
          aria-hidden="true"
        >
          ✦
        </span>
        <span
          className="pointer-events-none absolute right-[26%] top-[48%] -z-10 text-2xl text-indigo-500/35 animate-pulse [animation-delay:700ms]"
          aria-hidden="true"
        >
          ✦
        </span>
        <span
          className="pointer-events-none absolute left-[10%] bottom-[30%] -z-10 text-lg text-fuchsia-500/35 animate-pulse [animation-delay:1400ms]"
          aria-hidden="true"
        >
          ✦
        </span>
        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <ul className="grid gap-8 md:gap-10">
            {projects.map((p, i) => {
              const slug = internalToPublicSlug[p.slug];
              return (
                <li key={p.slug}>
                  <article id={`case-study-${slug}`} className="case-card group relative rounded-md transition-all duration-300 ease-out scroll-mt-24">
                    <div className="relative grid gap-0 overflow-hidden rounded-md md:grid-cols-12">
                      {/* Left column: image + buttons/stats row */}
                      <div className="flex flex-col md:col-span-8 gradient-border-split">
                        <Link
                          to="/work/$slug"
                          params={{ slug }}
                          aria-label={`View ${p.name} case study`}
                          className="card-img-sweep group/img relative block overflow-hidden"
                        >
                          <ResponsiveImg
                            picture={projectImages[p.slug]}
                            sizes="(min-width: 1024px) 880px, 100vw"
                            alt={`${p.name} cover artwork`}
                            loading={i === 0 ? "eager" : "lazy"}
                            fetchPriority={i === 0 ? "high" : "auto"}
                            className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover/img:scale-[1.02]"
                          />
                        </Link>

                        {/* Bottom row: buttons under left, stat cards under right */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gradient-border-t-2">
                          <div className="flex flex-col items-stretch justify-center gap-3 p-4 md:col-span-4 md:gradient-border-r-2 md:p-5">
                            <Link
                              to="/work/$slug"
                              params={{ slug }}
                              className="group/btn relative inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-hero px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-primary-foreground shadow-[0_8px_28px_-6px_oklch(0.62_0.24_28/0.55)] transition-all hover:-translate-y-0.5"
                              aria-label={`Open the ${p.name} case study`}
                            >
                              View case study
                              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/btn:rotate-45" />
                            </Link>

                            <a
                              href={p.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group/btn outline-hover-gradient relative inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-background px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground transition-all hover:-translate-y-0.5"
                              aria-label={`Visit ${p.name} website`}
                            >
                              Visit site
                              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/btn:rotate-45" />
                            </a>
                          </div>
                          <Link
                            to="/work/$slug"
                            params={{ slug }}
                            aria-label={`Open the ${p.name} case study`}
                            className="flex items-center md:col-span-8 bg-muted/20 p-3 md:p-4"
                          >
                            <ul className="grid w-full grid-cols-2 gap-2">
                              {p.metrics.slice(0, topMetricCount).map((m) => (
                                <li
                                  key={m.label}
                                  className="outline-hover-gradient relative rounded-md bg-background p-2.5 shadow-pop-sm transition-transform hover:-translate-y-0.5"
                                >
                                  <div className="font-display text-lg text-gradient leading-none">
                                    {m.value}
                                  </div>
                                  <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground leading-snug">
                                    {m.label}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </Link>

                        </div>
                      </div>

                      {/* Right column: content */}
                      <div className="flex flex-col justify-center p-6 md:col-span-4 md:p-8 lg:p-10">
                        <div className="flex items-center gap-3">
                          <div
                            className={`outline-hover-gradient relative flex shrink-0 items-center justify-center rounded-md bg-background p-1.5 shadow-pop-sm ${p.slug === "waypoint" ? "h-12 w-12" : p.slug === "techthings" ? "h-11 w-11" : "h-10 w-10"}`}
                          >
                            <img
                              src={projectLogos[p.slug]}
                              alt={`${p.name} logo`}
                              className="h-full w-full object-contain"
                              loading="lazy"
                              width={40}
                              height={40}
                            />
                          </div>
                          {projectExtraLogos[p.slug]?.map((src) => (
                            <div
                              key={src}
                              className="outline-hover-gradient relative flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background p-1.5 shadow-pop-sm"
                            >
                              <img
                                src={src}
                                alt={`${p.name} case study partner brand logo`}
                                className="h-full w-full object-contain"
                                loading="lazy"
                                width={32}
                                height={32}
                              />
                            </div>
                          ))}
                          <span className="m-0 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                            {p.year}
                          </span>

                        </div>

                        <h2 className="mt-4 font-display text-2xl leading-tight md:text-3xl">
                          <Link
                            to="/work/$slug"
                            params={{ slug }}
                            className="bg-gradient-hero bg-clip-text transition-colors duration-300 group-hover:text-transparent"
                          >
                            {p.name}
                          </Link>
                        </h2>
                        <p
                          className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em]"
                          style={{ color: "var(--color-electric)" }}
                        >
                          {p.sector}
                        </p>
                        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Role · {p.role}
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                          {p.summary}
                        </p>
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </>
  );
}
